import { Injectable } from '@angular/core';
import { DsChipColorVariant, DsChipIconPosition, Icons, IconStyles } from '@levelaccess/design-system';

import { $auditStandard, AuditStandards, mapAuditDbStandardToAuditStandard } from '@shared/constants/audit-standard';
import { $auditStandardSuccessCriteria } from '@shared/constants/audit-standard-success-criteria';
import { $flaw, flawStatus } from '@shared/constants/flaw';
import { $scanIssues } from '@shared/constants/scan-issues';
import { AccessibilityAuditToolNames } from '@shared/constants/audit-tool';
import { $successCriteria } from '@shared/audits/definitions/success-criteria/constants';
import { ISuccessCriteria } from '@shared/audits/definitions/success-criteria/success-criteria.interface';
import { IAutomatedScanIssue, IScanIssues, IScanIssuesSuccessCriteria } from '@shared/interfaces/scan-issues.interface';
import { SeverityChipUtility } from '@client/app/utility/severity-chip.utility';
import { SuccessCriteriaService } from '@client/app/services/success-criteria.service';
import { TranslateService } from '@client/app/translate/translate.service';
import { IFindingChipConfig, IFindingChipGroup } from '@client/app/interfaces/finding.interface';

export const FINDING_CRITERIA_AND_STANDARD_LABEL_KEY = 'audit_rule_property_success_criteria_criteria_and_standard';

@Injectable({ providedIn: 'root' })
export class FindingChipService {
  constructor(
    private successCriteriaService: SuccessCriteriaService,
    private translateService: TranslateService,
  ) {}

  public buildChips(
    issue: IAutomatedScanIssue,
    ruleDetails: IScanIssues,
    toolName: AccessibilityAuditToolNames | null,
  ): IFindingChipGroup[] {
    if (!issue || !ruleDetails) {
      return [];
    }

    const status: flawStatus = issue[$flaw.status];
    const severity: string = ruleDetails[$scanIssues.severity];
    const category: string = ruleDetails[$scanIssues.category] ?? '';
    const criteriaChips: IFindingChipConfig[] = this.buildCriteriaAndStandardChips(ruleDetails[$scanIssues.successCriteria]);

    const groups: (IFindingChipGroup | null)[] = [
      {
        labelKey: 'status',
        chips: [{ text: this.translateService.instant(`label_${status}`), colorVariant: this.getStatusChipVariant(status) }],
      },
      {
        labelKey: 'label_severity',
        chips: [
          {
            text: this.translateService.instant(`scan_issue_severity_${severity}`),
            colorVariant: SeverityChipUtility.getChipVariant(severity),
            showIcon: true,
            iconName: SeverityChipUtility.getIcon(severity),
            iconStyle: IconStyles.Regular,
            iconColor: SeverityChipUtility.getIconColor(severity),
            iconPosition: DsChipIconPosition.Left,
          },
        ],
      },
      {
        labelKey: 'label_tool',
        chips: [
          {
            text: this.translateService.instant(`scan_results_tool_option_${toolName}`),
            colorVariant: DsChipColorVariant.Neutral,
          },
        ],
      },
      {
        labelKey: 'audit_rule_property_ruleId',
        chips: [{ text: ruleDetails[$scanIssues.ruleId], colorVariant: DsChipColorVariant.Neutral }],
      },
      category ? { labelKey: 'category', chips: [{ text: category, colorVariant: DsChipColorVariant.Info }] } : null,
      criteriaChips.length ? { labelKey: FINDING_CRITERIA_AND_STANDARD_LABEL_KEY, chips: criteriaChips } : null,
    ];

    return groups.filter(
      (group: IFindingChipGroup): group is IFindingChipGroup =>
        group !== null && group.chips.some((c: IFindingChipConfig) => Boolean(c.text)),
    );
  }

  private buildCriteriaAndStandardChips(successCriteria: IScanIssuesSuccessCriteria[]): IFindingChipConfig[] {
    if (!successCriteria?.length) {
      return [];
    }
    const opensInNewTabLabel = this.translateService.instant('opens_in_new_tab_label');

    return successCriteria
      .map((criteria: IScanIssuesSuccessCriteria): IFindingChipConfig | null => {
        const auditStandardEntry = criteria[$auditStandardSuccessCriteria.auditStandards]?.[0];
        const standard: AuditStandards =
          mapAuditDbStandardToAuditStandard[auditStandardEntry?.[$auditStandard.standard]] ?? AuditStandards.wcag;
        const sc: ISuccessCriteria | null = this.successCriteriaService.getSuccessCriteriaFromStandard(
          standard,
          criteria[$auditStandardSuccessCriteria.identifier],
        );

        if (!sc) {
          return null;
        }

        const text = `${sc[$successCriteria.num]} ${sc[$successCriteria.handle]} – ${sc[$successCriteria.standard] ?? standard} ${sc[$successCriteria.versions]?.[0] ?? ''} ${sc[$successCriteria.level]}`;

        return {
          text,
          colorVariant: DsChipColorVariant.Primary,
          href: sc[$successCriteria.url],
          showIcon: true,
          iconName: Icons.ArrowUpRightFromSquare,
          iconStyle: IconStyles.Regular,
          iconPosition: DsChipIconPosition.Right,
          ariaLabel: `${text} ${opensInNewTabLabel}`,
        };
      })
      .filter((chip: IFindingChipConfig | null): chip is IFindingChipConfig => chip !== null);
  }

  private getStatusChipVariant(status: flawStatus): DsChipColorVariant {
    switch (status) {
      case flawStatus.closed:
        return DsChipColorVariant.Neutral;
      case flawStatus.dismissed:
        return DsChipColorVariant.Red;
      case flawStatus.open:
      case flawStatus.reOpen:
      default:
        return DsChipColorVariant.Success;
    }
  }
}
